import { Effect, Result } from "effect";
import {
  withoutVrijeDag,
  withVakantie,
  withVakantieregio,
  withVrijeDag,
  type Vakantie,
  type Vakantieregio,
  type VakantiesOverzicht,
  type VrijeDag,
} from "#/modules/les.planning/domain/vakanties";
import { PlanningApi, type PlanningApiError } from "#/modules/les.planning/services/PlanningApi";
import { PlanningCache } from "#/modules/les.planning/services/PlanningCache";
import { mutation, request } from "#/modules/les.planning/services/runtime";
import { recordUndo } from "#/modules/les.planning/services/UndoHistory";
import type { VrijeDagInput } from "#/modules/les.planning/vakanties.functions";
import { ApiFailureError } from "#/modules/util.errors/translate";

// The vrije periodes also grey the jaarplanning and the planning grids.
const refresh = PlanningCache.use((cache) =>
  cache.refresh("vakanties", "jaarplanning", "lesplanning"),
);

const show = (change: (overzicht: VakantiesOverzicht) => VakantiesOverzicht) =>
  PlanningCache.use((cache) =>
    Effect.flatMap(cache.vakanties, (overzicht) =>
      overzicht ? cache.setVakanties(change(overzicht)) : Effect.void,
    ),
  );

/** Shows `change` at once while `save` runs; the refetch after it has the school's own word. */
const optimistic = <A, E, R>(
  change: (overzicht: VakantiesOverzicht) => VakantiesOverzicht,
  save: Effect.Effect<A, E, R>,
) => show(change).pipe(Effect.andThen(save), Effect.ensuring(refresh));

/** The message of a vrije dag the server refused because it falls in another vrije periode. */
const getOverlap = (error: PlanningApiError) =>
  ApiFailureError.is(error.cause) && error.cause.key === "VrijeDagOverlapt"
    ? error.cause.message
    : null;

/** The form shows an overlap the server found after all under the date; anything else fails on. */
const overlapOrFail = (error: PlanningApiError) => {
  const overlap = getOverlap(error);
  return overlap === null ? Effect.fail(error) : Effect.succeed(overlap);
};

const toInput = ({ naam, start, eind }: VrijeDag): VrijeDagInput => ({ naam, start, eind });

export const changeVakantieregioAtom = mutation(
  Effect.fn("changeVakantieregio")(function* (vakantieregio: Vakantieregio) {
    const api = yield* PlanningApi;
    const cache = yield* PlanningCache;
    const previous = (yield* cache.vakanties)?.vakantieregio ?? null;
    if (previous === vakantieregio) return;
    const choose = (regio: Vakantieregio) =>
      optimistic((overzicht) => withVakantieregio(overzicht, regio), api.saveVakantieregio(regio));
    yield* choose(vakantieregio);
    // A school that had no regio cannot go back to none: the API only sets one.
    if (previous) yield* recordUndo({ undo: choose(previous), redo: choose(vakantieregio) });
  }),
);

/** Other dates for a schoolvakantie than the advice, or the advice again. */
export interface VakantieChange {
  readonly vakantie: Vakantie;
  readonly start: string;
  readonly eind: string;
}

export const changeVakantieAtom = mutation(
  Effect.fn("changeVakantie")(function* ({ vakantie, start, eind }: VakantieChange) {
    if (vakantie.start === start && vakantie.eind === eind) return;
    const api = yield* PlanningApi;
    const save = (dates: Pick<Vakantie, "start" | "eind">) =>
      optimistic(
        (overzicht) => withVakantie(overzicht, vakantie.naam, dates),
        api.saveVakantie({ naam: vakantie.naam, ...dates }),
      );
    yield* save({ start, eind });
    yield* recordUndo({
      undo: save({ start: vakantie.start, eind: vakantie.eind }),
      redo: save({ start, eind }),
    });
  }),
);

/** A new vrije dag; answers null once it is there, or the overlap that kept it out. */
export const addVrijeDagAtom = request(
  Effect.fn("addVrijeDag")(function* (input: VrijeDagInput) {
    const api = yield* PlanningApi;
    const created = yield* Effect.result(api.createVrijeDag(input));
    if (Result.isFailure(created)) return yield* overlapOrFail(created.failure);
    const vrijeDag: VrijeDag = { id: created.success.id, ...input };
    yield* show((overzicht) => withVrijeDag(overzicht, vrijeDag));
    yield* Effect.forkDetach(refresh);
    yield* recordUndo({
      undo: optimistic(
        (overzicht) => withoutVrijeDag(overzicht, vrijeDag.id),
        api.deleteVrijeDag(vrijeDag.id),
      ),
      redo: optimistic(
        (overzicht) => withVrijeDag(overzicht, vrijeDag),
        api.restoreVrijeDag(vrijeDag.id),
      ),
    });
    return null;
  }),
);

/** Another name or other dates for a vrije dag. */
export interface VrijeDagChange {
  readonly vrijeDag: VrijeDag;
  readonly input: VrijeDagInput;
}

/** Answers null once the vrije dag is changed, or the overlap that kept the change out. */
export const changeVrijeDagAtom = request(
  Effect.fn("changeVrijeDag")(function* ({ vrijeDag, input }: VrijeDagChange) {
    const api = yield* PlanningApi;
    const saved = yield* Effect.result(api.updateVrijeDag(vrijeDag.id, input));
    if (Result.isFailure(saved)) return yield* overlapOrFail(saved.failure);
    const changed: VrijeDag = { id: vrijeDag.id, ...input };
    yield* show((overzicht) => withVrijeDag(overzicht, changed));
    yield* Effect.forkDetach(refresh);
    const save = (next: VrijeDag) =>
      optimistic(
        (overzicht) => withVrijeDag(overzicht, next),
        api.updateVrijeDag(next.id, toInput(next)),
      );
    yield* recordUndo({ undo: save(vrijeDag), redo: save(changed) });
    return null;
  }),
);

const removeVrijeDag = (vrijeDag: VrijeDag) =>
  PlanningApi.use((api) =>
    optimistic(
      (overzicht) => withoutVrijeDag(overzicht, vrijeDag.id),
      api.deleteVrijeDag(vrijeDag.id),
    ),
  );

const bringBackVrijeDag = (vrijeDag: VrijeDag) =>
  PlanningApi.use((api) =>
    optimistic((overzicht) => withVrijeDag(overzicht, vrijeDag), api.restoreVrijeDag(vrijeDag.id)),
  );

export const deleteVrijeDagAtom = mutation(
  Effect.fn("deleteVrijeDag")(function* (vrijeDag: VrijeDag) {
    yield* removeVrijeDag(vrijeDag);
    yield* recordUndo({ undo: bringBackVrijeDag(vrijeDag), redo: removeVrijeDag(vrijeDag) });
  }),
);
